<template>
  <div class="courier-locations-map" :style="{ height }">
    <CAlert v-if="mapError" color="warning" class="mb-2 py-2">
      {{ mapError }}
    </CAlert>
    <div ref="map" class="courier-locations-map-canvas"></div>
  </div>
</template>

<script>
export default {
  name: 'CourierLocationsMap',
  props: {
    couriers: { type: Array, default: () => [] },
    hubs: { type: Array, default: () => [] },
    restaurant: { type: Object, default: null },
    order: { type: Object, default: null },
    height: { type: String, default: '420px' },
  },
  data() {
    return {
      map: null,
      infoWindow: null,
      markers: [],
      mapError: null,
    };
  },
  computed: {
    courierHubs() {
      const hubsById = new Map();

      [...this.hubs, ...this.couriers.flatMap(courier => courier.hubs || [])]
        .filter(hub => hub && this.hasCoordinates(hub.lat, hub.lng))
        .forEach((hub) => {
          hubsById.set(hub.id || `${hub.lat},${hub.lng}`, hub);
        });

      return Array.from(hubsById.values());
    },
    restaurantPosition() {
      return this.positionFrom(this.restaurant, [
        ['resto_lat', 'resto_lng'],
        ['restaurant_lat', 'restaurant_lng'],
        ['lat', 'lng'],
      ]);
    },
    customerPosition() {
      return this.positionFrom(this.order, [
        ['roLat', 'roLng'],
        ['lat', 'lng'],
      ]);
    },
  },
  watch: {
    couriers: {
      deep: true,
      handler() {
        this.renderMap();
      },
    },
    hubs: {
      deep: true,
      handler() {
        this.renderMap();
      },
    },
    restaurant: {
      deep: true,
      handler() {
        this.renderMap();
      },
    },
    order: {
      deep: true,
      handler() {
        this.renderMap();
      },
    },
  },
  mounted() {
    this.initMap();
    this.renderMap();
  },
  beforeDestroy() {
    this.clearMarkers();
  },
  methods: {
    initMap() {
      if (this.map || this.mapError) {
        return;
      }

      if (!window.google || !window.google.maps) {
        this.mapError = 'Google Maps is not loaded.';
        return;
      }

      this.map = new window.google.maps.Map(this.$refs.map, {
        zoom: 10,
        center: { lat: 50.8473094, lng: 4.3383618 },
        mapTypeControl: false,
        streetViewControl: false,
      });
      this.infoWindow = new window.google.maps.InfoWindow();
    },
    renderMap() {
      if (!this.map) {
        return;
      }

      this.clearMarkers();

      const bounds = new window.google.maps.LatLngBounds();
      let markerCount = 0;

      this.courierHubs.forEach((hub) => {
        markerCount += this.addMarker({
          position: { lat: parseFloat(hub.lat), lng: parseFloat(hub.lng) },
          title: hub.name || 'Hub',
          label: 'H',
          color: hub.delivery_disabled ? '#e55353' : '#6f42c1',
          content: this.hubInfoContent(hub),
          bounds,
        });
      });

      if (this.restaurantPosition) {
        markerCount += this.addMarker({
          position: this.restaurantPosition,
          title: this.restaurant?.restaurant_name || 'Restaurant',
          label: 'R',
          color: '#f9b115',
          content: this.restaurantInfoContent(),
          bounds,
        });
      }

      if (this.customerPosition) {
        markerCount += this.addMarker({
          position: this.customerPosition,
          title: 'Customer',
          label: 'C',
          color: '#0d6efd',
          content: this.customerInfoContent(),
          bounds,
        });
      }

      this.couriers
        .filter(courier => courier.location && this.hasCoordinates(courier.location.latitude, courier.location.longitude))
        .forEach((courier) => {
          markerCount += this.addMarker({
            position: {
              lat: parseFloat(courier.location.latitude),
              lng: parseFloat(courier.location.longitude),
            },
            title: `${courier.first_name || 'Courier'} #${courier.id}`,
            label: String(courier.id),
            color: this.courierColor(courier),
            content: this.courierInfoContent(courier),
            bounds,
          });
        });

      if (markerCount > 1) {
        this.map.fitBounds(bounds);
      } else if (markerCount === 1) {
        this.map.setCenter(bounds.getCenter());
        this.map.setZoom(13);
      }
    },
    addMarker({ position, title, label, color, content, bounds }) {
      if (!this.hasCoordinates(position.lat, position.lng)) {
        return 0;
      }

      const marker = new window.google.maps.Marker({
        map: this.map,
        position,
        title,
        label: {
          text: label,
          color: '#ffffff',
          fontSize: '11px',
          fontWeight: '700',
        },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: color,
          fillOpacity: 0.95,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });

      marker.addListener('click', () => {
        this.infoWindow.setContent(content);
        this.infoWindow.open(this.map, marker);
      });

      this.markers.push(marker);
      bounds.extend(position);

      return 1;
    },
    clearMarkers() {
      this.markers.forEach(marker => marker.setMap(null));
      this.markers = [];
    },
    courierColor(courier) {
      if (courier.location?.is_stale) {
        return '#6c757d';
      }

      if (courier.status?.on_break) {
        return '#f9b115';
      }

      if (courier.status?.on_order) {
        return '#2eb85c';
      }

      if (courier.status?.is_working_now) {
        return '#3399ff';
      }

      return '#e55353';
    },
    courierInfoContent(courier) {
      const vehicle = courier.vehicle
        ? `${this.escapeHtml(courier.vehicle.mode || '')} ${this.escapeHtml(courier.vehicle.license_plate || '')}`.trim()
        : 'No vehicle';
      const nearestHub = courier.nearest_hub
        ? `${this.escapeHtml(courier.nearest_hub.name)} (${this.formatDistance(courier.nearest_hub.distance_meters)})`
        : 'No hub distance';
      const mapsUrl = courier.location?.google_maps_url || `https://www.google.com/maps?q=${courier.location.latitude},${courier.location.longitude}`;

      return `
        <div class="courier-map-info">
          <strong>${this.escapeHtml(courier.first_name || 'Courier')} #${courier.id}</strong>
          <div>${vehicle}</div>
          <div>Status: ${this.escapeHtml(this.courierStatusLabel(courier))}</div>
          <div>Last GPS: ${this.escapeHtml(this.locationAgeLabel(courier.location))}</div>
          <div>Nearest hub: ${nearestHub}</div>
          <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer">Open in Google Maps</a>
        </div>
      `;
    },
    hubInfoContent(hub) {
      return `
        <div class="courier-map-info">
          <strong>${this.escapeHtml(hub.name || 'Hub')} #${hub.id || ''}</strong>
          <div>${this.escapeHtml(hub.zip || '')}</div>
          <div>${hub.delivery_disabled ? 'Delivery disabled' : 'Delivery enabled'}</div>
        </div>
      `;
    },
    restaurantInfoContent() {
      return `
        <div class="courier-map-info">
          <strong>${this.escapeHtml(this.restaurant?.restaurant_name || 'Restaurant')}</strong>
          <div>${this.escapeHtml(this.restaurant?.restaurant_postcode || '')}</div>
        </div>
      `;
    },
    customerInfoContent() {
      return `
        <div class="courier-map-info">
          <strong>Customer</strong>
          <div>${this.escapeHtml(this.order?.roAddress || '')}</div>
          <div>${this.escapeHtml([this.order?.roPostal_code, this.order?.roCity].filter(Boolean).join(' '))}</div>
        </div>
      `;
    },
    courierStatusLabel(courier) {
      if (courier.location?.is_stale) return 'stale location';
      if (courier.status?.on_break) return 'on break';
      if (courier.status?.on_order) return 'on order';
      if (courier.status?.is_working_now) return 'working';
      return 'not working';
    },
    locationAgeLabel(location) {
      if (!location || location.age_minutes === null || typeof location.age_minutes === 'undefined') {
        return 'unknown';
      }

      if (location.age_minutes < 1) {
        return 'just now';
      }

      return `${location.age_minutes} min ago${location.is_stale ? ' (stale)' : ''}`;
    },
    formatDistance(meters) {
      if (meters === null || typeof meters === 'undefined') {
        return '-';
      }

      if (meters < 1000) {
        return `${Math.round(meters)} m`;
      }

      return `${(meters / 1000).toFixed(2)} km`;
    },
    positionFrom(item, keys) {
      if (!item) {
        return null;
      }

      for (const [latKey, lngKey] of keys) {
        if (this.hasCoordinates(item[latKey], item[lngKey])) {
          return {
            lat: parseFloat(item[latKey]),
            lng: parseFloat(item[lngKey]),
          };
        }
      }

      return null;
    },
    hasCoordinates(latitude, longitude) {
      return latitude !== null
        && longitude !== null
        && typeof latitude !== 'undefined'
        && typeof longitude !== 'undefined'
        && !Number.isNaN(parseFloat(latitude))
        && !Number.isNaN(parseFloat(longitude));
    },
    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },
  },
};
</script>

<style scoped>
.courier-locations-map {
  min-height: 320px;
  position: relative;
  width: 100%;
}
.courier-locations-map-canvas {
  height: 100%;
  min-height: 320px;
  width: 100%;
}
</style>

<style>
.courier-map-info {
  color: #2c384a;
  font-size: 12px;
  line-height: 1.45;
  min-width: 180px;
}
.courier-map-info a {
  display: inline-block;
  margin-top: 4px;
}
</style>
